import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, CircleMarker, useMapEvents, useMap, Tooltip } from "react-leaflet";
import LocationSidebar from "./LocationSidebar";
import { useLocation } from "react-router-dom";

// --- Leaflet Icon Fix (For the 'Add New' Pin) ---
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const RAW_API_BASE = process.env.REACT_APP_API_BASE || "/api";
const API_BASE = String(RAW_API_BASE).replace(/\/+$/, "");
const SIDEBAR_WIDTH = 280;

const STAGE_LEGEND = [
  { name: "Not Started", color: "#6c757d" },
  { name: "Survey", color: "#0d6efd" },
  { name: "Valuation", color: "#0dcaf0" },
  { name: "Negotiation", color: "#6610f2" },
  { name: "Payment", color: "#fd7e14" },
  { name: "Acquired", color: "#198754" },
  { name: "Demolished", color: "#dc3545" },
];

const TILE_OPTIONS = {
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenStreetMap contributors',
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
  },
  dark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; CARTO',
  },
};

const MapLegend = () => (
  <div className="leaflet-bottom leaflet-right" style={{ marginBottom: "20px", marginRight: "10px", pointerEvents: "auto" }}>
    <div className="bg-white p-2 rounded shadow-sm" style={{ minWidth: "140px", border: "2px solid rgba(0,0,0,0.2)" }}>
      <h6 className="fw-bold mb-2 small text-uppercase text-muted border-bottom pb-1">Legend</h6>
      <div className="d-flex flex-column gap-1">
        {STAGE_LEGEND.map((item) => (
          <div key={item.name} className="d-flex align-items-center">
            <span
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: item.color,
                borderRadius: "50%",
                display: "inline-block",
                marginRight: "8px",
                border: "1px solid rgba(0,0,0,0.2)"
              }}
            />
            <span style={{ fontSize: "0.75rem", fontWeight: "600", color: "#343a40" }}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// --- click-to-add marker support ---
function LocationMarker({ isAdding, onLocationFound }) {
  useMapEvents({
    click(e) {
      if (isAdding) onLocationFound(e.latlng);
    },
  });
  return null;
}

// --- Independent FlyTo Component ---
function MapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 16, { duration: 1.5 });
  }, [center, map, zoom]);
  return null;
}

// --- Draggable Marker Component (Defined OUTSIDE Map to prevent re-renders) ---
const DraggableActiveMarker = ({ activeLocation, onDragEnd, isAdmin }) => {
  const markerRef = useRef(null);
  
  // Sync position if activeLocation coordinates change (e.g. via sidebar input)
  useEffect(() => {
    if (markerRef.current && activeLocation) {
      const { lat, lng, x_coord, y_coord } = activeLocation;
      const finalLat = parseFloat(lat || x_coord);
      const finalLng = parseFloat(lng || y_coord);
      
      if (!isNaN(finalLat) && !isNaN(finalLng)) {
        const current = markerRef.current.getLatLng();
        // Only set if diff is significant to avoid drag loops
        if (Math.abs(current.lat - finalLat) > 0.000001 || Math.abs(current.lng - finalLng) > 0.000001) {
             markerRef.current.setLatLng([finalLat, finalLng]);
        }
      }
    }
  }, [activeLocation]);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          onDragEnd(marker.getLatLng());
        }
      },
    }),
    [onDragEnd]
  );

  if (!activeLocation) return null;

  const lat = activeLocation.lat || activeLocation.x_coord;
  const lng = activeLocation.lng || activeLocation.y_coord;

  if (!lat || !lng) return null;

  return (
    <Marker
      draggable={isAdmin || !activeLocation.uuid} // Admin can edit existing, any user can move "new" pin before saving
      eventHandlers={eventHandlers}
      position={[parseFloat(lat), parseFloat(lng)]}
      ref={markerRef}
      zIndexOffset={1000} // Ensure it sits on top
    />
  );
};

export default function Map() {
  const [tileKey, setTileKey] = useState("street");
  const location = useLocation();

  // User State
  const [currentUser, setCurrentUser] = useState(null);
  const isAdmin = currentUser?.role === "admin";

  // map data
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(
    STAGE_LEGEND.map(s => s.name)
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // sidebar / active location
  const [activeLocation, setActiveLocation] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  
  // Separate state for where the map should fly to
  // This prevents the map from "fighting" the user while dragging a marker
  const [flyToTarget, setFlyToTarget] = useState(null);

  // adding mode
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [inputMethod, setInputMethod] = useState("pin");
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [rawGeoJson, setRawGeoJson] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // preview cache
  const [previewCache, setPreviewCache] = useState({});
  const hoveredRef = useRef(null); 

  // --- FETCH USER ---
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/me`, { headers: { "Authorization": `Bearer ${token}` } });
        if (res.ok) setCurrentUser(await res.json());
      } catch (e) {
        console.error("Error fetching user", e);
      }
    };
    fetchUser();
  }, []);

  const fetchLocations = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/locations`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setLocations(Array.isArray(data) ? data : []);
      } else {
        console.warn("/locations returned", res.status);
      }
    } catch (err) {
      console.error("Failed to fetch map locations", err);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, [fetchLocations]);

  // fetch single location details for hover
  const fetchLocationPreview = useCallback(async (loc) => {
    if (!loc) return;
    const key = loc.uuid || loc.building_id;
    if (!key) return;
    if (previewCache[key]) return previewCache[key];

    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/location/${encodeURIComponent(loc.building_id)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        console.warn("preview fetch failed", res.status, loc.building_id);
        return null;
      }
      const data = await res.json();
      const cacheKey = data.uuid || loc.uuid || loc.building_id;
      setPreviewCache(prev => ({ ...prev, [cacheKey]: data }));
      return data;
    } catch (e) {
      console.warn("preview fetch error", e);
      return null;
    }
  }, [previewCache]);

  useEffect(() => {
    // Check if we navigated here with state
    if (location.state && location.state.focusOn) {
      const target = location.state.focusOn;

      const lat = parseFloat(target.x_coord || target.lat);
      const lng = parseFloat(target.y_coord || target.lng);

      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        setActiveLocation({
          ...target,
          lat: lat,
          lng: lng,
          uuid: target.uuid,
          building_id: target.id || target.building_id
        });
        // Center Map
        setFlyToTarget({ lat, lng });
        setShowSidebar(true);
        
        window.history.replaceState({}, document.title);
      }
    }
  }, [location]);

  // handlers & utilities
  const resetAddState = () => {
    setIsAddingMode(false);
    setManualLat("");
    setManualLng("");
    setRawGeoJson("");
    setErrorMsg("");
  };

  const toggleAddMode = () => {
    if (isAddingMode) {
      resetAddState();
      setActiveLocation(null);
      setShowSidebar(false);
    } else {
      setIsAddingMode(true);
      setIsFilterOpen(false); // Close filter if open
    }
  };

  const toggleFilterMenu = () => {
    if(isFilterOpen) {
      setIsFilterOpen(false);
    } else {
      setIsFilterOpen(true);
      setIsAddingMode(false); // Close add mode if open
    }
  };

  const handleStatusFilterChange = (statusName) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusName)) {
        return prev.filter(s => s !== statusName);
      } else {
        return [...prev, statusName];
      }
    });
  };

  const handleSelectAllFilters = () => {
    if (selectedStatuses.length === STAGE_LEGEND.length) {
        setSelectedStatuses([]); // Deselect All
    } else {
        setSelectedStatuses(STAGE_LEGEND.map(s => s.name)); // Select All
    }
  };

  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setActiveLocation(null);
    if (isAddingMode) resetAddState();
    fetchLocations();
  };

  const handleMapClick = (latlng) => {
    if (inputMethod !== "pin") return;
    const newPoint = { lat: latlng.lat, lng: latlng.lng };
    setActiveLocation(newPoint);
    setShowSidebar(true);
  };

  const handleMarkerClick = (loc) => {
    const lat = parseFloat(loc.x_coord);
    const lng = parseFloat(loc.y_coord);

    const newActive = {
        ...loc,
        lat: lat,
        lng: lng
    };
    
    setActiveLocation(newActive);
    setFlyToTarget({ lat, lng }); // Update Camera ONLY on click
    setShowSidebar(true);
    setIsAddingMode(false);
  };

  const handleManualCoords = () => {
    const lat = parseFloat(manualLat), lng = parseFloat(manualLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) { setErrorMsg("Invalid Latitude or Longitude"); return; }
    setErrorMsg("");
    setActiveLocation({ lat, lng });
    setFlyToTarget({ lat, lng });
    setShowSidebar(true);
  };

  const handleGeoJsonSubmit = () => {
    try {
      const parsed = JSON.parse(rawGeoJson);
      setErrorMsg("");
      const layer = L.geoJSON(parsed);
      const bounds = layer.getBounds();
      const center = bounds.getCenter();
      setActiveLocation({ lat: center.lat, lng: center.lng });
      setFlyToTarget({ lat: center.lat, lng: center.lng });
      setShowSidebar(true);
    } catch (e) {
      setErrorMsg("Invalid GeoJSON format");
    }
  };

  const handleSaveLocation = (data) => {
    fetchLocations();
  };

  const handleMarkerMouseOver = (loc) => async () => {
    const key = loc.uuid || loc.building_id;
    hoveredRef.current = key;
    if (!previewCache[key]) {
      setPreviewCache(prev => ({ ...prev, [key]: { __loading: true } }));
      const data = await fetchLocationPreview(loc);
      if (hoveredRef.current === key) {
        if (!data) setPreviewCache(prev => ({ ...prev, [key]: null }));
      }
    }
  };

  // --- DRAG HANDLER ---
  const handleDragEnd = useCallback((newLatLng) => {
    setActiveLocation((prev) => ({
      ...prev,
      // Update both Leaflet props and Database props so Sidebar catches it
      lat: newLatLng.lat,
      lng: newLatLng.lng,
      x_coord: String(newLatLng.lat), // Ensure string format for API compatibility
      y_coord: String(newLatLng.lng)
    }));
    // NOTE: We do NOT setFlyToTarget here, so the map stays still while dragging
  }, []);

  const handleMarkerMouseOut = (loc) => () => {
    const key = loc.uuid || loc.building_id;
    if (hoveredRef.current === key) hoveredRef.current = null;
  };

  // --- FILTERING LOGIC ---
  const visibleLocations = useMemo(() => {
    let filtered = locations;

    // 1. Status Filter
    if (selectedStatuses.length !== STAGE_LEGEND.length) {
        filtered = filtered.filter(loc => selectedStatuses.includes(loc.current_status));
    }

    // 2. Search Filter (Building ID, Status, or Address if available)
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(loc => {
        const bldId = (loc.building_id || "").toLowerCase();
        const status = (loc.current_status || "").toLowerCase();
        const cached = previewCache[loc.uuid || loc.building_id];
        const addr = (loc.address || (cached && cached.address) || "").toLowerCase();

        return bldId.includes(s) || status.includes(s) || addr.includes(s);
      });
    }

    return filtered;
  }, [locations, selectedStatuses, searchTerm, previewCache]);

  return (
    <div style={{ position: "fixed", top: 0, bottom: 0, left: SIDEBAR_WIDTH, right: 0, zIndex: 1 }}>

      {/* Controls */}
      <div style={{
        position: "absolute", top: "12px", right: "12px", zIndex: 1000,
        display: "flex", flexDirection: "column", gap: "10px", alignItems: "end", pointerEvents: "none"
      }}>
        {/* Search Bar */}
        <div style={{ pointerEvents: "auto", position: 'relative' }}>
            <input 
                type="text" 
                placeholder="Search ID, Status, Address..." 
                className="form-control shadow-sm"
                style={{ borderRadius: '20px', paddingLeft: '35px', width: '250px' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="bi bi-search text-muted" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}></i>
        </div>

        {/* Tile Selector */}
        <div style={{ pointerEvents: "auto", background: "rgba(255,255,255,0.9)", padding: "6px 10px", borderRadius: "8px", boxShadow: "0 2px 6px rgba(0,0,0,0.2)" }}>
          <select value={tileKey} onChange={(e) => setTileKey(e.target.value)} style={{ border: "none", background: "transparent", fontWeight: "600", outline: "none", cursor: "pointer" }}>
            <option value="street">Street View</option>
            <option value="satellite">Satellite</option>
            <option value="dark">Dark Mode</option>
          </select>
        </div>

        {/* Action Buttons Row */}
        <div className="d-flex gap-2" style={{pointerEvents: 'auto'}}>
            
            {/* Filter Toggle Button */}
            <button
                onClick={toggleFilterMenu}
                className={`btn ${isFilterOpen ? "btn-dark" : "btn-light text-dark"}`}
                style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "30px", padding: "10px 16px", fontWeight: "bold", border: 'none' }}
                title="Filter map by status"
            >
                <i className={`bi ${isFilterOpen ? 'bi-funnel-fill' : 'bi-funnel'} me-2`}></i> 
                Filter
            </button>

            {/* Add Location Button - ONLY ADMIN */}
            {isAdmin && (
                <button
                    onClick={toggleAddMode}
                    className={`btn ${isAddingMode ? "btn-danger" : "btn-primary"}`}
                    style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.3)", borderRadius: "30px", padding: "10px 20px", fontWeight: "bold", transition: "all 0.3s" }}
                >
                    {isAddingMode ? (<><i className="bi bi-x-circle me-2"></i> Cancel</>) : (<><i className="bi bi-geo-alt-fill me-2"></i> Add Location</>)}
                </button>
            )}
        </div>

        {/* Filter Menu Popup */}
        {isFilterOpen && !showSidebar && (
            <div className="bg-white p-3 rounded shadow-lg" style={{ pointerEvents: "auto", width: "240px", marginTop: "5px" }}>
                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="m-0 fw-bold small text-uppercase">Filter Status</h6>
                    <span 
                        className="small text-primary" 
                        style={{cursor: 'pointer', fontSize: '0.75rem'}}
                        onClick={handleSelectAllFilters}
                    >
                        {selectedStatuses.length === STAGE_LEGEND.length ? "Clear All" : "Select All"}
                    </span>
                </div>
                <div className="d-flex flex-column gap-2">
                    {STAGE_LEGEND.map((status) => (
                        <label key={status.name} className="d-flex align-items-center gap-2" style={{fontSize: '0.85rem', cursor: 'pointer'}}>
                            <input 
                                type="checkbox" 
                                className="form-check-input mt-0"
                                checked={selectedStatuses.includes(status.name)}
                                onChange={() => handleStatusFilterChange(status.name)}
                            />
                            <span 
                                style={{
                                    width: "8px",
                                    height: "8px",
                                    backgroundColor: status.color,
                                    borderRadius: "50%",
                                    display: "inline-block"
                                }}
                            />
                            {status.name}
                        </label>
                    ))}
                </div>
            </div>
        )}

        {/* Add Location Menu Popup - ONLY ADMIN */}
        {isAdmin && isAddingMode && !showSidebar && (
          <div className="bg-white p-3 rounded shadow-lg" style={{ pointerEvents: "auto", width: "320px", marginTop: "10px" }}>
            <div className="btn-group w-100 mb-3" role="group">
              <button type="button" className={`btn btn-sm ${inputMethod === "pin" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setInputMethod("pin")}>Pin</button>
              <button type="button" className={`btn btn-sm ${inputMethod === "coords" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setInputMethod("coords")}>Coords</button>
            </div>

            {errorMsg && <div className="alert alert-danger py-1 px-2 mb-2 small">{errorMsg}</div>}

            {inputMethod === "pin" && <div className="text-center text-muted small py-2">Click map to drop pin.</div>}

            {inputMethod === "coords" && (
              <div className="d-flex flex-column gap-2">
                <input type="number" className="form-control form-control-sm" placeholder="Latitude" value={manualLat} onChange={(e) => setManualLat(e.target.value)} />
                <input type="number" className="form-control form-control-sm" placeholder="Longitude" value={manualLng} onChange={(e) => setManualLng(e.target.value)} />
                <button className="btn btn-sm btn-dark w-100 mt-1" onClick={handleManualCoords}>Plot Point</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Map */}
      <MapContainer center={[-6.2088, 106.8456]} zoom={13} style={{ width: "100%", height: "100%", cursor: (isAddingMode && inputMethod === "pin") ? "crosshair" : "grab" }} zoomControl={false}>
        <TileLayer key={tileKey} url={TILE_OPTIONS[tileKey].url} attribution={TILE_OPTIONS[tileKey].attribution} />
        
        {/* Controlled FlyTo */}
        <MapFlyTo center={flyToTarget} />
        
        <LocationMarker isAdding={isAddingMode && inputMethod === "pin"} onLocationFound={handleMapClick} />

        {/* RENDER ACTIVE DRAGGABLE MARKER */}
        {activeLocation && showSidebar && (
           <DraggableActiveMarker 
              activeLocation={activeLocation} 
              onDragEnd={handleDragEnd} 
              isAdmin={isAdmin || !activeLocation.uuid} // Admin can edit existing, any user can move "new" pin before saving
           />
        )}

        {/* RENDER INACTIVE MARKERS (DOTS) - NOW RENDERED EVEN IF ACTIVE */}
        {visibleLocations.map((loc) => {
          const lat = parseFloat(loc.x_coord), lng = parseFloat(loc.y_coord);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

          const cacheKey = loc.uuid || loc.building_id;
          const preview = previewCache[cacheKey];

          return (
            <CircleMarker
              key={loc.uuid || `${loc.building_id}-${lat}-${lng}`}
              center={[lat, lng]}
              radius={10}
              pathOptions={{ color: "white", weight: 2, fillColor: loc.status_color || "#6c757d", fillOpacity: 1 }}
              eventHandlers={{
                click: () => handleMarkerClick(loc),
                mouseover: handleMarkerMouseOver(loc),
                mouseout: handleMarkerMouseOut(loc),
              }}
            >
              <Tooltip direction="top" offset={[0, -12]} opacity={1} interactive className="custom-hover-tooltip">
                <div style={{
                  minWidth: 200,
                  background: "white",
                  borderRadius: 8,
                  padding: "8px 10px",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
                  color: "#212529",
                  pointerEvents: "auto"
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{loc.building_id}</div>

                  <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 8 }}>
                    {preview ? (preview.__loading ? "Loading..." : (preview.address || "")) : "Loading..."}
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-start" }}>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: "#0d6efd",
                        color: "white",
                        borderRadius: 8,
                        padding: "4px 10px",
                        fontWeight: 700,
                        boxShadow: "none"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // This uses handleMarkerClick logic to set flyToTarget properly
                        handleMarkerClick(loc);
                      }}
                    >
                      {loc.current_status || "Open"}
                    </button>
                  </div>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}

        <MapLegend />
      </MapContainer>

      <LocationSidebar isOpen={showSidebar} onClose={handleCloseSidebar} activeData={activeLocation} onSave={handleSaveLocation} />
    </div>
  );
}